import React, { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ZodEffects, ZodObject, ZodRawShape, z } from "zod";

import { useForm } from "../../hooks/useForm";
import { DealerVinResultsRoute } from "../../navigation/DealerRoutes";
import { useRouteParams } from "../../navigation/useRouteParams";
import { useUrl } from "../../stores/hooks/useUrl";
import { OrderDetails } from "../../stores/slices/cartSlice";
import { createHashStateHook } from "../../stores/useHashState";
import { createQueryStringHook } from "../../stores/useQueryState";
import { PrimaryButton, PrimaryOutlinedButton } from "../../ui/Button/Button";
import { Dialog } from "../../ui/Dialog/Dialog";
import { DialogHeader } from "../../ui/Dialog/DialogHeader";
import { Input } from "../../ui/Input/Input";
import { Textarea } from "../../ui/Textarea/Textarea";

export type LeadFormModalType =
  | typeof cartActionDownload
  | typeof cartActionEmail
  | typeof cartActionPrint
  | typeof cartActionSaveQuote;

type LeadFormModalProps = {
  cart?: OrderDetails;
  generatedByPrefix: string;
  hook: ReturnType<typeof useLeadFormModalState>;
  redirectAction: ((leadFormData: LeadFormData) => void) | null;
  submitAction: (
    form: LeadParams,
    type: LeadFormModalType,
  ) => Promise<LeadFormData>;
  subtitle: ReactNode;
  title: ReactNode;
  type: LeadFormModalType;
};

export const cartActionDownload = "download";
export const cartActionEmail = "email";
export const cartActionPrint = "print";
export const cartActionSaveQuote = "save-quote";

export const useLeadFormModalState = createHashStateHook("#lead-form");

export const customerMessageParamName = "customerMessage";
const useCustomerMessage = createQueryStringHook(customerMessageParamName, "");

const labelClasses = "block mb-1 text-xs text-ink-2 font-light";
const inputClasses = "w-full";

const createSchema = (
  type: LeadFormModalType,
): ZodEffects<ZodObject<ZodRawShape>> =>
  z
    .object({
      customerEmail: z.union([
        z.literal(""),
        z.string().email({ message: "form.error.customerEmail" }),
      ]),
      customerFirstName: z.string(),
      customerLastName: z.string(),
      customerMessage: z.string(),
      customerPhone: z.string(),
      customerVin: z.string(),
      generatedBy: z.string(),
    })
    .refine((data) => type !== cartActionEmail || !!data.customerEmail, {
      message: "form.error.customerEmail",
      path: ["customerEmail"],
    });

export type LeadFormData = z.infer<ReturnType<typeof createSchema>>;

export type LeadParams = LeadFormData & {
  sendCustomerEmail: boolean;
};

export const LeadFormModal: FunctionComponent<LeadFormModalProps> = ({
  cart,
  generatedByPrefix,
  hook,
  redirectAction,
  submitAction,
  subtitle,
  title,
  type,
}) => {
  const [visible, { hide }] = hook;
  const { t } = useTranslation();
  const [customerMessage] = useCustomerMessage();
  const [[isSumitting, isEmailAction], setBusy] = useState<boolean[]>([]);
  const vinParams = useRouteParams(DealerVinResultsRoute);
  const { searchParams } = useUrl();
  const defaultCustomerVin =
    cart?.customerVin || vinParams?.vehicleVin || searchParams.get("vin") || "";

  useEffect(() => {
    if (visible && window.adobeLauncherUrl) {
      window.runAdobeFormStart();
    }
  }, [visible]);

  const action = async (data: LeadFormData): Promise<void> => {
    redirectAction && redirectAction(data);
    hide();
  };
  const schema = createSchema(type);
  const [handleForm] = useForm({
    onSubmit(
      {
        customerEmail,
        customerFirstName,
        customerLastName,
        customerMessage,
        customerPhone,
        customerVin,
        generatedBy,
      },
      event,
    ) {
      const submitEvent = event.nativeEvent as SubmitEvent;
      const submitter = submitEvent.submitter as HTMLButtonElement | undefined;
      const sendCustomerEmail = submitter?.name === "send";
      setBusy([true, sendCustomerEmail]);
      submitAction(
        {
          customerEmail,
          customerFirstName,
          customerLastName,
          customerMessage,
          customerPhone,
          customerVin,
          generatedBy,
          sendCustomerEmail,
        },
        type,
      )
        .then((data) => {
          if (window.adobeLauncherUrl) {
            window.runAdobeFormComplete();
          }
          return action(data);
        })
        .finally(() => {
          setBusy([]);
        });
    },
    schema,
  });
  const idEmail = `email-${type}`;
  const idFirstName = `first-name-${type}`;
  const idLastName = `last-name-${type}`;
  const idNotes = `notes-${type}`;
  const idPhone = `phone-${type}`;
  const idVin = `vin-${type}`;
  const idForm = `form-${type}`;
  return (
    <Dialog
      className="w-full lg:max-w-[48rem] sm:p-4 md:p-8"
      id="lead-form-modal"
      onClose={hide}
      visible={visible}
    >
      <DialogHeader onClose={hide} subtitle={subtitle} title={title} />
      <form
        className="p-4 grid sm:grid-cols-2 gap-4 md:gap-8 mt-4"
        id={idForm}
        noValidate
        onSubmit={handleForm}
      >
        <fieldset>
          <label className={labelClasses} htmlFor={idFirstName}>
            {t("form.label.firstName")}
          </label>
          <Input
            aria-label="yourFirstName"
            autoComplete="given-name"
            className={inputClasses}
            defaultValue={cart?.customerName || ""}
            id={idFirstName}
            name="customerFirstName"
            placeholder={t("form.placeholder.firstName")}
          />
        </fieldset>
        <fieldset>
          <label className={labelClasses} htmlFor={idLastName}>
            {t("form.label.lastName")}
          </label>
          <Input
            aria-label="yourLastName"
            autoComplete="family-name"
            className={inputClasses}
            id={idLastName}
            name="customerLastName"
            placeholder={t("form.placeholder.lastName")}
          />
        </fieldset>
        <fieldset>
          <label className={labelClasses} htmlFor={idEmail}>
            {t("form.label.customerEmail")}
          </label>
          <Input
            aria-label="yourEmail"
            autoCapitalize="off"
            autoComplete="email"
            className={inputClasses}
            id={idEmail}
            name="customerEmail"
            placeholder={t("form.placeholder.email")}
            type="email"
          />
        </fieldset>
        <fieldset>
          <label className={labelClasses} htmlFor={idPhone}>
            {t("form.label.phoneNumber")}
          </label>
          <Input
            aria-label="yourPhoneNumber"
            autoCapitalize="off"
            autoComplete="phone-number"
            className={inputClasses}
            id={idPhone}
            inputMode="tel"
            name="customerPhone"
            placeholder={t("form.placeholder.phoneNumber")}
          />
        </fieldset>
        <fieldset>
          <label className={labelClasses} htmlFor={idVin}>
            {t("form.label.vin")}
          </label>
          <Input
            aria-label="selectedVin"
            autoCapitalize="characters"
            autoComplete="off"
            className={inputClasses}
            defaultValue={defaultCustomerVin}
            id={idVin}
            name="customerVin"
            placeholder={t("form.placeholder.vin")}
          />
        </fieldset>
        <fieldset className="sm:col-span-2 w-full">
          <label className={labelClasses} htmlFor={idNotes}>
            {t("form.label.notes")}
          </label>
          <Textarea
            aria-label="yourNotes"
            autoComplete="off"
            className="w-full"
            defaultValue={customerMessage}
            id={idNotes}
            key={customerMessage}
            name="customerMessage"
            placeholder={t("form.placeholder.notes")}
          />
        </fieldset>
        <input
          name="generatedBy"
          type="hidden"
          value={`${generatedByPrefix}${type}`}
        />
      </form>
      <footer className="p-4 flex items-center justify-end space-x-1">
        {![cartActionEmail, cartActionSaveQuote].includes(type) && (
          <PrimaryOutlinedButton
            aria-label="leadFormSecondaryAction"
            // onClick={action}
            busy={isSumitting && !isEmailAction}
            disabled={isSumitting}
            form={idForm}
            type="submit"
          >
            {t(`leadForm.button.${type}.secondary`)}
          </PrimaryOutlinedButton>
        )}
        <PrimaryButton
          aria-label="leadFormPrimaryAction"
          busy={isSumitting && isEmailAction}
          disabled={isSumitting}
          form={idForm}
          name="send"
          type="submit"
        >
          {t(`leadForm.button.${type}.primary`)}
        </PrimaryButton>
      </footer>
    </Dialog>
  );
};

/**
 * @see docs/worklog/TW-874-comparison-lead-link/rfc.md
 * @see docs/worklog/TW-874-comparison-lead-link/tfc.md
 * @see docs/worklog/TW-876-print-validation/rfc.md
 * @see docs/worklog/TW-876-print-validation/tfc.md
 * @see docs/worklog/TW-880-gm-adobe-script/rfc.md
 * @see docs/worklog/TW-880-gm-adobe-script/tfc.md
 */
